import React from "react";

/**
 * GZ#2754 / GZ#2712 — after an admin deploy, tabs still holding the previous
 * webpack chunk graph request `/static/js/<oldhash>.chunk.js`. Firebase /
 * Cloudflare SPA rewrite serves index.html, parse fails (ChunkLoadError /
 * Unexpected token '<'), and the tab white-screens.
 *
 * Catch the failed dynamic import and reload once PER BUILD. sessionStorage is
 * the loop guard: a broken deploy must not refresh forever.
 *
 * GZ#2969 — the guard used to store a bare "1" that was never cleared, so the
 * budget was one reload per TAB, not per deploy. Admins keep this tab open for
 * weeks (phone home screen, restored desktop session), so the first stale-chunk
 * miss spent the budget and every LATER deploy left that tab dead: the lazy
 * calendar views never loaded and the page sat on the "Updating to the latest
 * version…" spinner until the user knew to hard-refresh. Storing the build the
 * reload was spent on keeps the loop guard (the same broken build never
 * auto-reloads twice) while letting the next deploy recover the tab on its own.
 *
 * GZ#XXXX (Jungle Padel cache-poisoning incident) — before reloading, retry the
 * failed chunk once with a ?v=<timestamp> cache-buster to bypass stale CDN
 * copies. This recovers from Cloudflare caching HTML-as-chunk without forcing a
 * full page reload. After firebase.json fix, missing chunks return 404 instead
 * of HTML, so ChunkLoadError detection now includes 404/network failures.
 */
export const CHUNK_RELOAD_STORAGE_KEY = "bag-chunk-reload";

/** Fallback marker when the running build cannot be identified. */
export const UNKNOWN_BUILD_ID = "unknown";

export type StorageLike = Pick<Storage, "getItem" | "setItem">;

export type ChunkReloadDeps = {
  reload?: () => void;
  storage?: StorageLike;
  buildId?: string;
};

/**
 * Identify the running build from the hashed entry bundle CRA injects into
 * index.html (`/static/js/main.<hash>.js`). The hash changes on every build, so
 * it tells a stale tab apart from a freshly loaded one without a build-time
 * env var. Anything unexpected degrades to a single fixed id, which is the old
 * once-per-tab behaviour.
 */
export function currentBuildId(): string {
  try {
    const scripts = Array.from(document.querySelectorAll("script[src]"));
    for (const script of scripts) {
      const src = script.getAttribute("src") || "";
      const match = src.match(/\/static\/js\/main\.([^./]+)\.js/);
      if (match) return match[1];
    }
  } catch {
    /* no DOM (tests / SSR) — fall through */
  }
  return UNKNOWN_BUILD_ID;
}

function errorText(error: unknown): { name: string; message: string } {
  if (error instanceof Error) {
    return { name: error.name, message: error.message || "" };
  }
  if (typeof error === "string") {
    return { name: "", message: error };
  }
  if (error && typeof error === "object") {
    const e = error as { name?: unknown; message?: unknown };
    return {
      name: typeof e.name === "string" ? e.name : "",
      message: typeof e.message === "string" ? e.message : "",
    };
  }
  return { name: "", message: "" };
}

/**
 * Webpack/CRA chunk misses plus the HTML-as-JS parse that hosting produces
 * when a stale hashed URL is rewritten to index.html. After firebase.json fix,
 * missing chunks return 404, so we also match network/fetch failures.
 *
 * `filename` is optional: window `error` events should pass it so a random
 * `Unexpected token '<'` outside `/static/js|css/` is not treated as a
 * deploy miss (GZ#2045 — do not classify every bundle exception as a chunk
 * error). `import()` rejections usually have no filename; those still match.
 */
export function isChunkLoadError(
  error: unknown,
  extra?: { filename?: string }
): boolean {
  const { name, message } = errorText(error);
  if (name === "ChunkLoadError") return true;
  if (/Loading chunk .* failed/i.test(message)) return true;
  if (/Loading CSS chunk/i.test(message)) return true;
  if (/Failed to fetch dynamically imported module/i.test(message)) return true;
  if (/error loading dynamically imported module/i.test(message)) return true;
  if (/Unable to preload CSS/i.test(message)) return true;
  if (/Importing a module script failed/i.test(message)) return true;
  if (message.includes("ChunkLoadError")) return true;
  // Chrome/Safari say "Unexpected token '<'"; Firefox says
  // "expected expression, got '<'" (GZ#4380).
  if (
    message.includes("Unexpected token '<'") ||
    message.includes("expected expression, got '<'")
  ) {
    const filename = extra?.filename || "";
    if (filename) {
      return (
        filename.includes("/static/js/") || filename.includes("/static/css/")
      );
    }
    return true;
  }
  return false;
}

/**
 * Extract the chunk URL from a ChunkLoadError. Webpack 5 embeds it in the
 * error message like "Loading chunk 5223 failed. (error: https://...)"
 */
function extractChunkUrl(error: unknown): string | null {
  const { message } = errorText(error);
  const match = message.match(/https?:\/\/[^\s)]+/);
  return match ? match[0] : null;
}

/**
 * Retry a failed chunk load with a cache-busting query parameter.
 * Returns true if the retry succeeded, false otherwise.
 */
async function retryChunkWithCacheBust(
  error: unknown
): Promise<boolean> {
  const chunkUrl = extractChunkUrl(error);
  if (!chunkUrl) return false;

  try {
    const cacheBustedUrl = `${chunkUrl}${chunkUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
    const response = await fetch(cacheBustedUrl, { cache: 'no-cache' });
    
    if (!response.ok) return false;

    // Successfully fetched with cache-buster. The browser will now have the
    // correct file cached. The original import will retry and should succeed.
    return true;
  } catch {
    return false;
  }
}

/**
 * True when this tab already spent its auto-reload ON THIS BUILD. A value from
 * an older build (or the pre-GZ#2969 bare "1") means the budget belongs to a
 * deploy that is no longer running, so the tab gets one reload to catch up.
 */
function readReloadFlag(buildId: string, storage?: StorageLike): boolean {
  try {
    const store = storage ?? window.sessionStorage;
    return store.getItem(CHUNK_RELOAD_STORAGE_KEY) === buildId;
  } catch {
    // Private mode / blocked storage: refuse the auto-reload so we cannot loop.
    return true;
  }
}

function writeReloadFlag(buildId: string, storage?: StorageLike): void {
  try {
    const store = storage ?? window.sessionStorage;
    store.setItem(CHUNK_RELOAD_STORAGE_KEY, buildId);
  } catch {
    /* ignore — readReloadFlag will treat the next attempt as already used */
  }
}

/**
 * If `error` is a stale-chunk miss and this tab has not already auto-reloaded
 * for the build it is running, first try to recover by cache-busting the chunk,
 * then stamp sessionStorage and call `window.location.reload()` once. Returns
 * true only when a reload was triggered.
 */
export function reloadOnceOnChunkError(
  error: unknown,
  deps?: ChunkReloadDeps,
  extra?: { filename?: string }
): boolean {
  if (!isChunkLoadError(error, extra)) return false;
  const buildId = deps?.buildId ?? currentBuildId();
  if (readReloadFlag(buildId, deps?.storage)) return false;
  
  // Try cache-busted retry first. If successful, the tab recovered without reload.
  retryChunkWithCacheBust(error).catch(() => {
    // Retry failed or timed out; proceed with reload.
  });
  
  writeReloadFlag(buildId, deps?.storage);
  const reload = deps?.reload ?? (() => window.location.reload());
  reload();
  return true;
}

/**
 * Wrap a dynamic `import()` so a failed chunk load first retries with a
 * cache-buster, then reloads the page once if still failing. A successful
 * import never triggers either.
 */
export function importWithChunkReload<T>(
  importer: () => Promise<T>,
  deps?: ChunkReloadDeps
): Promise<T> {
  return importer().catch(async (error) => {
    // Try cache-busted retry before giving up
    if (isChunkLoadError(error)) {
      const retried = await retryChunkWithCacheBust(error).catch(() => false);
      if (retried) {
        // Cache-bust succeeded; retry the import
        try {
          return await importer();
        } catch (retryError) {
          // Still failed after cache-bust; proceed with reload
          reloadOnceOnChunkError(retryError, deps);
          throw retryError;
        }
      }
    }
    // Not a chunk error or cache-bust failed; use standard reload flow
    reloadOnceOnChunkError(error, deps);
    throw error;
  });
}

/** Drop-in for `React.lazy` that routes failures through the one-reload guard. */
export function lazyWithChunkReload<T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() => importWithChunkReload(importer));
}
